// src/contexts/AuthContext.tsx
import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
  } from "react";
  import {
    User,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    updateProfile,
    sendPasswordResetEmail,
  } from "firebase/auth";
  import { doc, setDoc, getDoc, updateDoc } from "firebase/firestore";
  import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
  import { auth, db, storage } from "../firebase/config";
  
  type UserRole = "user" | "specialist" | "admin";
  
  interface UserProfile {
    uid: string;
    email: string | null;
    displayName: string | null;
    photoURL: string | null;
    role: UserRole;
    bio?: string;
  }
  
  interface AuthContextType {
    currentUser: User | null;
    userProfile: UserProfile | null;
    loading: boolean;
    error: string | null;
    signUp: (email: string, password: string, name: string) => Promise<void>;
    login: (email: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
    resetPassword: (email: string) => Promise<void>;
    updateUserProfile: (data: {
      displayName: string;
      bio: string;
      photoFile?: File;
    }) => Promise<void>;
    isAdmin: boolean;
    isSpecialist: boolean;
  }
  
  const defaultContext: AuthContextType = {
    currentUser: null,
    userProfile: null,
    loading: true,
    error: null,
    signUp: async () => {},
    login: async () => {},
    logout: async () => {},
    resetPassword: async () => {},
    updateUserProfile: async () => {},
    isAdmin: false,
    isSpecialist: false,
  };
  
  const AuthContext = createContext<AuthContextType>(defaultContext);
  
  export const useAuth = () => useContext(AuthContext);
  
  interface AuthProviderProps {
    children: ReactNode;
  }
  
  export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
  
    useEffect(() => {
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        setCurrentUser(user);
  
        if (user) {
          try {
            // Get user profile from Firestore
            const userDoc = await getDoc(doc(db, "users", user.uid));
  
            if (userDoc.exists()) {
              const userData = userDoc.data() as Omit<UserProfile, "uid">;
              setUserProfile({
                uid: user.uid,
                email: user.email,
                displayName: user.displayName,
                photoURL: user.photoURL,
                role: userData.role || "user",
                bio: userData.bio || "",
              });
            } else {
              // If user document doesn't exist, create a default one
              setUserProfile({
                uid: user.uid,
                email: user.email,
                displayName: user.displayName,
                photoURL: user.photoURL,
                role: "user",
                bio: "",
              });
              await setDoc(doc(db, "users", user.uid), {
                role: "user",
                createdAt: new Date().toISOString(),
                bio: "",
              });
            }
          } catch (err) {
            console.error("Error fetching user profile:", err);
            setError("Failed to load user profile");
          }
        } else {
          setUserProfile(null);
        }
  
        setLoading(false);
      });
  
      return unsubscribe;
    }, []);
  
    const signUp = async (email: string, password: string, name: string) => {
      try {
        setError(null);
        setLoading(true);
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          password
        );
        const user = userCredential.user;
  
        // Update display name in Firebase Auth
        await updateProfile(user, { displayName: name });
  
        // Create user document in Firestore
        await setDoc(doc(db, "users", user.uid), {
          role: "user",
          createdAt: new Date().toISOString(),
          bio: "",
        });
      } catch (err: any) {
        console.error("Signup error:", err);
        setError(err.message || "Failed to sign up");
        throw err;
      } finally {
        setLoading(false);
      }
    };
  
    const login = async (email: string, password: string) => {
      try {
        setError(null);
        setLoading(true);
        await signInWithEmailAndPassword(auth, email, password);
      } catch (err: any) {
        console.error("Login error:", err);
        setError(err.message || "Failed to log in");
        throw err;
      } finally {
        setLoading(false);
      }
    };
  
    const logout = async () => {
      try {
        setError(null);
        await signOut(auth);
      } catch (err: any) {
        console.error("Logout error:", err);
        setError(err.message || "Failed to log out");
        throw err;
      }
    };
  
    const resetPassword = async (email: string) => {
      try {
        setError(null);
        setLoading(true);
        await sendPasswordResetEmail(auth, email);
      } catch (err: any) {
        console.error("Password reset error:", err);
        setError(err.message || "Failed to send password reset email");
        throw err;
      } finally {
        setLoading(false);
      }
    };
  
    // New updateUserProfile function with file upload integration
    const updateUserProfile = async (data: {
      displayName: string;
      bio: string;
      photoFile?: File;
    }) => {
      if (!currentUser) return;
      try {
        setLoading(true);
        let newPhotoURL = userProfile?.photoURL || "";
        if (data.photoFile) {
          // Create a reference in Storage
          const photoRef = ref(
            storage,
            `profiles/${currentUser.uid}/${data.photoFile.name}`
          );
          // Upload the file
          await uploadBytes(photoRef, data.photoFile);
          // Get the download URL
          newPhotoURL = await getDownloadURL(photoRef);
        }
  
        // Update Firebase Auth profile
        await updateProfile(currentUser, {
          displayName: data.displayName,
          photoURL: newPhotoURL,
        });
  
        // Update Firestore profile document
        await updateDoc(doc(db, "users", currentUser.uid), {
          displayName: data.displayName,
          bio: data.bio,
          photoURL: newPhotoURL,
        });
  
        // Update local state with new values
        setUserProfile((prev) =>
          prev
            ? {
                ...prev,
                displayName: data.displayName,
                bio: data.bio,
                photoURL: newPhotoURL,
              }
            : prev
        );
      } catch (err: any) {
        console.error("Profile update failed:", err);
        setError(err.message || "Failed to update profile");
        throw err;
      } finally {
        setLoading(false);
      }
    };
  
    const isAdmin = userProfile?.role === "admin";
    const isSpecialist = userProfile?.role === "specialist" || isAdmin;
  
    return (
      <AuthContext.Provider
        value={{
          currentUser,
          userProfile,
          loading,
          error,
          signUp,
          login,
          logout,
          resetPassword,
          updateUserProfile,
          isAdmin,
          isSpecialist,
        }}
      >
        {children}
      </AuthContext.Provider>
    );
  };
  
  export default AuthContext;
  